import React from 'react';
import { MapPin, Globe, Calendar, Users, Clock, Star, CheckCircle, Award, Languages } from 'lucide-react';

interface Education {
  degree: string;
  institution: string;
  year: number;
  field: string;
}

interface License {
  type: string;
  number: string;
  state: string;
  expirationDate: string;
  isActive: boolean;
}

interface Insurance {
  name: string;
  accepted: boolean;
}

interface TreatmentModality {
  name: string;
  accepted: boolean;
}

interface ClientPresentation {
  name: string;
  accepted: boolean;
}

interface PreferredAge {
  name: string;
  accepted: boolean;
}

interface SessionType {
  type: string;
  duration: number;
}

interface Rate {
  sessionType: string;
  amount: number;
  currency: string;
}

interface Language {
  name: string;
  accepted: boolean;
}

interface Specialty {
  id: string;
  name: string;
  _id: string;
}

interface Address {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface TimeSlot {
  start: string;
  end: string;
  available: boolean;
}

interface WeeklySchedule {
  monday: TimeSlot[];
  tuesday: TimeSlot[];
  wednesday: TimeSlot[];
  thursday: TimeSlot[];
  friday: TimeSlot[];
  saturday: TimeSlot[];
  sunday: TimeSlot[];
}

interface Availability {
  openSpots: number;
  weeklySchedule: WeeklySchedule;
  timezone: string;
}

interface CaseLoad {
  currentClients: number;
  targetWeeklyClients: number;
  maxCapacity: number;
  activeClients: number;
  inactiveClients: number;
}

interface Profile {
  bio: string;
  education: Education[];
  licenses: License[];
  insuranceAccepted: Insurance[];
  treatmentModalities: TreatmentModality[];
  clientPresentation: ClientPresentation[];
  preferredAges: PreferredAge[];
  sessionTypes: SessionType[];
  rates: Rate[];
  languages: Language[];
  profileImage?: string;
}

interface Clinician {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  title: string;
  gender: string;
  phone: string;
  website: string;
  specialties: Specialty[];
  verificationStatus: string;
  billingStatus: string;
  availability: Availability;
  address: Address;
  caseLoad: CaseLoad;
  profile: Profile;
  practiceId: string;
  isActive: boolean;
  __v: number;
  createdAt: string;
  updatedAt: string;
}

interface ClinicianDetailsProps {
  clinician: Clinician;
}

const ClinicianDetails: React.FC<ClinicianDetailsProps> = ({ clinician }) => {
  if (!clinician) return null;

  const fullName = `${clinician.firstName} ${clinician.lastName}`;
  const acceptedInsurance = clinician.profile.insuranceAccepted.filter(ins => ins.accepted);
  const spokenLanguages = clinician.profile.languages.filter(lang => lang.accepted);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="flex flex-col lg:flex-row items-start gap-12">
            {/* Profile Image */}
            <div className="relative group">
              {clinician.profile.profileImage ? (
                <img 
                  src={clinician.profile.profileImage} 
                  alt={fullName}
                  className="w-64 h-64 rounded-3xl object-cover shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105"
                />
              ) : (
                <div className="w-64 h-64 rounded-3xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <span className="text-6xl font-bold text-white">
                    {clinician.firstName[0]}{clinician.lastName[0]}
                  </span>
                </div>
              )}
              {clinician.verificationStatus === 'verified' && (
                <div className="absolute -top-2 -right-2 bg-green-500 dark:bg-green-400 rounded-full p-2 shadow-md">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              )}
            </div>

            {/* Profile Info */}
            <div className="flex-1 space-y-6">
              <div>
                <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">
                  {fullName}
                </h1>
                <p className="text-2xl text-gray-600 dark:text-gray-300 font-medium transition-colors duration-300">{clinician.title}</p>
              </div>

              {/* Contact Info */}
              <div className="flex flex-wrap gap-4">
                {clinician.website && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full transition-colors duration-300">
                    <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-gray-700 dark:text-gray-200">Website</span>
                  </div>
                )}
                {clinician.address && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full transition-colors duration-300">
                    <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-gray-700 dark:text-gray-200">{clinician.address.city}, {clinician.address.state}</span>
                  </div>
                )}
              </div>

              {/* Specialties */}
              <div className="flex flex-wrap gap-3">
                {clinician.specialties.map((specialty) => (
                  <span 
                    key={specialty._id}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700 text-white rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-shadow duration-300"
                  >
                    {specialty.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12 bg-white dark:bg-gray-900 transition-colors duration-300">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* About Section */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl p-8 shadow-sm hover:shadow-md transition-all duration-300">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3 transition-colors duration-300">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700 rounded-lg"></div>
                About
              </h2>
              <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed transition-colors duration-300">
                {clinician.profile.bio}
              </p>
            </div>

            {/* Education */}
            {clinician.profile.education && clinician.profile.education.length > 0 && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl p-8 shadow-sm hover:shadow-md transition-all duration-300">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3 transition-colors duration-300">
                  <Award className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  Education
                </h2>
                <div className="space-y-6">
                  {clinician.profile.education.map((edu, index) => (
                    <div key={index} className="p-6 bg-gray-50 dark:bg-gray-700 rounded-2xl border border-gray-100 dark:border-gray-600 transition-colors duration-300">
                      <p className="text-xl font-bold text-gray-900 dark:text-white transition-colors duration-300">
                        {edu.degree} {edu.field && `in ${edu.field}`}
                      </p>
                      <p className="text-gray-600 dark:text-gray-300 text-lg transition-colors duration-300">{edu.institution}</p>
                      <p className="text-blue-600 dark:text-blue-400 font-medium mt-2 transition-colors duration-300">Graduated {edu.year}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Session Types */}
            {clinician.profile.sessionTypes && clinician.profile.sessionTypes.length > 0 && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl p-8 shadow-sm hover:shadow-md transition-all duration-300">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3 transition-colors duration-300">
                  <Clock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  Session Types
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {clinician.profile.sessionTypes.map((session, index) => (
                    <div 
                      key={index}
                      className="p-6 bg-gray-50 dark:bg-gray-700 rounded-2xl border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      <p className="font-bold text-xl capitalize text-gray-900 dark:text-white mb-2 transition-colors duration-300">
                        {session.type} Sessions
                      </p>
                      <p className="text-gray-600 dark:text-gray-300 text-lg transition-colors duration-300">
                        {session.duration} minutes
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Quick Info */}
          <div className="space-y-8">
            {/* Availability */}
            {clinician.availability && (
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 dark:from-green-600 dark:to-emerald-700 rounded-3xl p-8 shadow-md text-white transition-colors duration-300">
                <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                  <Calendar className="w-6 h-6" />
                  Availability
                </h3>
                <div className="text-center">
                  <p className="text-4xl font-bold mb-2">{clinician.availability.openSpots}</p>
                  <p className="text-green-100 text-lg">Open Spots Available</p>
                </div>
              </div>
            )}

            {/* Rates */}
            {clinician.profile.rates && clinician.profile.rates.length > 0 && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl p-8 shadow-sm hover:shadow-md transition-all duration-300">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">Session Rates</h3>
                <div className="space-y-4">
                  {clinician.profile.rates.map((rate, index) => (
                    <div 
                      key={index}
                      className="flex justify-between items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-100 dark:border-gray-600 transition-colors duration-300"
                    >
                      <span className="capitalize text-gray-700 dark:text-gray-200 font-medium transition-colors duration-300">
                        {rate.sessionType}
                      </span>
                      <span className="font-bold text-xl text-gray-900 dark:text-white transition-colors duration-300">
                        ${rate.amount}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Insurance */}
            {acceptedInsurance.length > 0 && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl p-8 shadow-sm hover:shadow-md transition-all duration-300">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">Insurance Accepted</h3>
                <div className="space-y-3">
                  {acceptedInsurance.map((insurance, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-100 dark:border-gray-600 transition-colors duration-300"
                    >
                      <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400" />
                      <span className="text-gray-700 dark:text-gray-200 font-medium transition-colors duration-300">{insurance.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Languages */}
            {spokenLanguages.length > 0 && (
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-gray-200/50">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Languages className="w-6 h-6 text-blue-600" />
                  Languages
                </h3>
                <div className="flex flex-wrap gap-3">
                  {spokenLanguages.map((language, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800 rounded-full text-sm font-medium border border-purple-200"
                    >
                      {language.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Caseload Stats */}
            {clinician.caseLoad && (
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-gray-200/50">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Users className="w-6 h-6 text-blue-600" />
                  Practice Stats
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Active Clients</span>
                    <span className="font-bold text-gray-900">{clinician.caseLoad.activeClients}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Max Capacity</span>
                    <span className="font-bold text-gray-900">{clinician.caseLoad.maxCapacity}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${(clinician.caseLoad.activeClients / clinician.caseLoad.maxCapacity) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicianDetails;